import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import React from "react";
import BackBtn from "../../widgets/BackBtn";
import useApiEp from "../../hooks/useApiEp";
import { Download } from "@mui/icons-material";
import * as XLSX from "xlsx";

const UserResponses = () => {
  const { data, isLoading, isError, error } = useApiEp(`/rcs/user-responses`);

  // Function to export data to Excel
  const exportToExcel = () => {
    if (!data || data.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(
      data.map((item) => ({
        "Message ID": item.messageId,
        From: item.from,
        Message: item.body,
        "Reply Type": item.replyType,
        "Date & Time": new Date(item.createdOn).toLocaleString(),
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "User Responses");
    XLSX.writeFile(
      workbook,
      `RCS_User_Responses_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  // Function to get color for reply type
  const getReplyTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "positive":
        return "success";
      case "negative":
        return "error";
      case "neutral":
        return "default";
      default:
        return "primary";
    }
  };

  if (isLoading) {
    return (
      <Container
        sx={{
          mt: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container sx={{ mt: 3 }}>
        <BackBtn />
        <Alert severity="error" sx={{ mt: 2 }}>
          Error loading user responses: {error?.message || "Unknown error"}
        </Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 3, mb: 4 }}>
      <BackBtn />

      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontWeight: "bold", color: "primary.main" }}
          >
            RCS Campaign Responses
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: "600px" }}
          >
            View and manage all user responses from your RCS marketing campaign.
            Monitor engagement, analyze feedback, and export data for further
            analysis.
          </Typography>
        </Box>

        {/* Export Button */}
        {data && data.length > 0 && (
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={exportToExcel}
            sx={{ minWidth: "140px" }}
          >
            Export Excel
          </Button>
        )}
      </Box>

      {/* Responses Count */}
      {data && data.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" color="text.secondary">
            Total Responses: <strong>{data.length}</strong>
          </Typography>
        </Box>
      )}

      {/* Responses Table */}
      <TableContainer component={Paper} elevation={2}>
        {!data || data.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary">
              No responses found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              User responses from your RCS campaign will appear here.
            </Typography>
          </Box>
        ) : (
          <Table sx={{ minWidth: 650 }} aria-label="user responses table">
            <TableHead sx={{ backgroundColor: "primary.main" }}>
              <TableRow>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Message ID
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  From
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Message
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Reply Type
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                  Date & Time
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((response, index) => (
                <TableRow
                  key={response.messageId || index}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ fontFamily: "monospace" }}
                  >
                    {response.messageId}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace" }}>
                    {response.from}
                  </TableCell>
                  <TableCell
                    sx={{ maxWidth: "300px", wordBreak: "break-word" }}
                  >
                    {response.body}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={response.replyType || "Text reply"}
                      color={getReplyTypeColor(response.replyType)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(response.createdOn).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Additional Info */}
      {data && data.length > 0 && (
        <Box sx={{ mt: 2, p: 2, backgroundColor: "grey.50", borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Tip:</strong> Use the export feature to download all
            responses for detailed analysis in Excel.
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default UserResponses;
